import invariant from 'invariant';
import { isNumber } from 'lodash';
import type { Asset, Comment, Share } from '@frameio/graphql-client/generated';
import { isUUID } from '@frameio/utils/formatters';
// @todo (WEB-1045): Add condensed UUID support to routes
// import { toCondensedUUID } from '@frameio/utils/formatters/uuid';
import { createUrl } from '../helpers/createUrl';
import { getPathnameFromHref } from '../helpers/getPathnameFromHref';
import type { ValidHref } from '../types';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

/**
 * ShareReviewLandingPage
 *
 * (root)
 * /share/:shareId
 *
 * (inside folder)
 * /share/:shareId/:folderId
 */
type ShareReviewLandingPage =
  | {
      page: 'shareReviewFinder';
      shareId: Share['id'];
      root?: boolean;
    }
  | {
      page: 'shareReviewFinder';
      shareId: Share['id'];
      folderId: Asset['id'];
    }
  | {
      page: 'shareReview';
      shareId: Share['id'];
      root?: boolean;
      assetId?: Asset['id'];
      print?: boolean;
    };

/**
 * ShareReviewViewerPage
 * /share/:shareId/view/:assetId
 */
export type ShareReviewViewerPage = {
  page: 'shareReviewFinder';
  shareId: Share['id'];
  assetId: Asset['id'];
  commentId?: Comment['id'];
  versionId?: Asset['id'];
  activeIndex?: number;
  compare?: string[];
  print?: boolean;
  /**
   * Deeplinking to a specific time in the media viewer (microseconds)
   */
  t?: number;
};

export type ShareReviewGetLinkParams =
  | ShareReviewLandingPage
  | ShareReviewViewerPage;

export const getLinkForShareReview = (
  params: ShareReviewGetLinkParams
): ValidHref => {
  // Assert required params for better error messages
  invariant(params.shareId, 'shareId is required');
  const shareId = toCondensedUUID(params.shareId);

  // /share/:shareId/view/:assetId(?c=:commentId)(version=:versionId)
  if ('assetId' in params && params.assetId) {
    const assetId = toCondensedUUID(params.assetId);

    const urlSearchParams = new URLSearchParams();
    let newUrl = `/share/${shareId}/view/${assetId}`;

    if (params.print) {
      return createUrl({
        pathname: `/share/${shareId}/print/comments/${params.assetId}`,
      });
    }

    if ('commentId' in params && params.commentId) {
      urlSearchParams.set('c', params.commentId);
    }

    if ('versionId' in params && params.versionId) {
      urlSearchParams.set('version', params.versionId);
    }

    // ... ?activeIndex=:activeIndex
    if ('activeIndex' in params && isNumber(params.activeIndex)) {
      urlSearchParams.set('activeIndex', params.activeIndex.toString());
    }

    // ... ?compare=:comparisonId1&compare=comparisonId2
    if ('compare' in params && params.compare) {
      params.compare.forEach((param) => {
        urlSearchParams.append('compare', toCondensedUUID(param));
      });
    }

    // ... ?t=:t
    if ('t' in params && isNumber(params.t)) {
      urlSearchParams.set('t', params.t.toString());
    }

    if (urlSearchParams.toString()) {
      newUrl += `?${
        urlSearchParams.toString() as {
          c?: string;
          version?: string;
          compare?: string;
          activeIndex?: string;
          t?: string;
        }
      }`;
    }
    return newUrl as ValidHref;
  }

  if ('folderId' in params && params.folderId) {
    // /share/:shareId/:folderId
    const folderId = toCondensedUUID(params.folderId);
    return createUrl({
      pathname: `/share/${shareId}/${folderId}`,
    });
  }

  const slash = (params as any).root ? '/' : '';
  // /share/:shareId (Matches page: shareReview for when the layout is unknown)
  return createUrl({
    pathname: `/share/${shareId}${slash}`,
  });
};

export const isShareReviewFinderPageType = (href: ValidHref): boolean => {
  const pathname = getPathnameFromHref(href);
  const parts = pathname.split('/');
  if (parts[1] !== 'share') return false;

  // /share/:shareId
  if (parts.length === 3 && isUUID(parts[2])) return true;

  // /share/:shareId/
  if (parts.length === 4 && isUUID(parts[2]) && parts[3] === '') return true;

  // /share/:shareId/:folderId
  if (parts.length === 4 && isUUID(parts[2]) && isUUID(parts[3])) return true;

  // /share/:shareId/view/:assetId
  if (parts.length === 5 && isUUID(parts[2]) && parts[3] === 'view')
    return true;

  // /share/:shareId/view/:assetId/
  if (
    parts.length === 6 &&
    isUUID(parts[2]) &&
    parts[3] === 'view' &&
    parts[5] === ''
  )
    return true;

  // /share/:shareId/print/comments/:assetId
  if (
    parts.length === 6 &&
    isUUID(parts[2]) &&
    parts[3] === 'print' &&
    parts[4] === 'comments'
  )
    return true;

  return false;
};
